import { useElementScrollable } from '@/app/hooks/useDetectScrollAppear';
import { BoxProps } from '@mui/material';
import { Box, useTheme } from '@mui/material';
import { forwardRef, useLayoutEffect, useRef } from 'react';

interface MUIBoxScrollBehaviorProps extends BoxProps {
    enableDragScroll?: boolean; // Optional prop to enable/disable drag-to-scroll
    showScrollWhenHover?: boolean; // Optional prop to show scrollbar only when hovering
    hideScrollbar?: boolean; // Optional prop to hide scrollbar
}

const MUIBoxScrollBehavior = forwardRef<HTMLDivElement, MUIBoxScrollBehaviorProps>((props, ref) => {
    const { enableDragScroll = false, showScrollWhenHover = false, hideScrollbar = false, ...restProps } = props; // Default values
    const theme = useTheme();
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const handleMouseDown = (event: React.MouseEvent) => {
        if (!enableDragScroll) return; // Exit if drag-to-scroll is not enabled

        const container = scrollContainerRef.current;
        if (!container) return;

        const startX = event.pageX - container.offsetLeft;
        const scrollLeft = container.scrollLeft;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const x = moveEvent.pageX - container.offsetLeft;
            const walk = (x - startX) * 2; // Multiplier for scroll speed
            container.scrollLeft = scrollLeft - walk;
        };

        const handleMouseUp = () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const isScrollable = useElementScrollable(scrollContainerRef);

    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            // Additional logic if needed when component mounts
        }
    }, [isScrollable]);
    return (
        <Box
            id={props?.id ?? ''}
            ref={(node) => {
                scrollContainerRef.current = node as HTMLDivElement | null;
                if (ref) {
                    if (typeof ref === 'function') {
                        ref(node as HTMLDivElement | null);
                    } else if ('current' in ref) {
                        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node as HTMLDivElement | null;
                    }
                }
            }}
            onMouseDown={handleMouseDown}
            sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                ...(hideScrollbar ? {
                    '&::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                } : {
                    '&::-webkit-scrollbar': { width: '.5rem', height: '.5rem' },
                    '&::-webkit-scrollbar-track': { background: theme.palette.background.default },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#C8CFD5',
                        borderRadius: '8px',
                        visibility: showScrollWhenHover ? 'hidden' : 'visible',
                    },
                    '&:hover::-webkit-scrollbar-thumb': showScrollWhenHover ? { visibility: 'visible' } : undefined,
                }),
                ...restProps.sx, // Allows for additional styles to be passed in
            }}
        >
            {props.children}
        </Box>
    );
});

export default MUIBoxScrollBehavior;
